import CloseIcon from '@mui/icons-material/Close';
import { Box, styled } from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import { AppleIcon } from '@/assets';
import { Capacitor } from '@capacitor/core';
import { LoggerService } from '@/shared/logger';
import { Download } from 'lucide-react';

const openDownloadLink = (
  url = 'https://apps.apple.com/ca/app/bhabi/id6475659322'
) => {
  window.open(url, '_blank');
};

export function usePWAInstall() {
  const [canInstall, setCanInstall] = useState(false);
  const deferredPromptRef = React.useRef<any>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      setCanInstall(true);
      LoggerService.info('PWA beforeinstallprompt event triggered');
    };

    // Listen for the beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', handler);

    // Also check app installed state
    window.addEventListener('appinstalled', () => {
      LoggerService.info('PWA installed successfully');
      setCanInstall(false);
      deferredPromptRef.current = null;
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', () => {});
    };
  }, []);

  const install = async () => {
    if (!deferredPromptRef.current) {
      LoggerService.warn('No deferred prompt available');
      return;
    }

    try {
      deferredPromptRef.current.prompt();
      const result = await deferredPromptRef.current.userChoice;

      if (result.outcome === 'accepted') {
        LoggerService.info('PWA installed by user');
      } else {
        LoggerService.info('PWA installation dismissed by user');
      }
    } catch (error) {
      LoggerService.error('PWA installation error:', error);
    }

    deferredPromptRef.current = null;
    setCanInstall(false);
  };

  return { canInstall, install };
}

export const DownloadLink: FC = () => {
  const [hide, setHide] = React.useState(true);
  const { canInstall, install } = usePWAInstall();

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      setHide(true);
      return;
    }
    const time = setTimeout(() => {
      setHide(false);
    }, 200);

    return () => {
      clearTimeout(time);
    };
  }, []);

  if (hide) return null;

  return (
    <aside className="bh-download" aria-label="Get the Bhabi app">
      <span className="bh-download__text">Play Bhabi on your phone</span>
      <button
        type="button"
        className="bh-btn bh-btn--secondary bh-btn--small"
        onClick={() => openDownloadLink()}
      >
        <AppleIcon fontSize="small" aria-hidden="true" />
        App Store
      </button>
      {canInstall ? (
        <button
          type="button"
          className="bh-btn bh-btn--secondary bh-btn--small"
          onClick={() => install()}
        >
          <Download size={16} aria-hidden="true" />
          Install
        </button>
      ) : null}
      <button
        type="button"
        className="bh-icon-btn"
        aria-label="Dismiss app download"
        onClick={() => setHide(true)}
      >
        <CloseIcon fontSize="small" />
      </button>
    </aside>
  );
};

export const DownloadLinkWrapper = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  margin: '0 auto',
  backgroundColor: theme.palette.grey[800],
  color: theme.palette.secondary.contrastText,
}));

export const DownloadLinkInnerWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(1),
  minHeight: 110,
}));
